import { Panel } from "@/components/portal/widgets";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, X } from "lucide-react";
import { fmtDateTime } from "@/lib/time";
import { SAMPLE_PREFIX } from "@/lib/sampleMart";

export const MartPreview = ({ data, filename, committing, onCommit, onReset, skip = 0 }) => {
  const trips = data.trips || [];
  const total = data.count ?? trips.length;
  const fresh = data.new_count ?? trips.filter((t) => !t._exists).length;
  const existing = data.existing_count ?? total - fresh;
  const isSample = trips.length > 0 && trips.every((t) => String(t.external_trip_id || "").startsWith(SAMPLE_PREFIX));
  return (
    <Panel title={`MART manifest detected · ${total} trips`} testid="import-mart-preview"
      action={<button onClick={onReset} className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200" data-testid="import-reset"><X className="h-3.5 w-3.5" /> Start over</button>}>
      <div className="flex flex-wrap gap-3 px-5 pt-4 text-xs" data-testid="import-mart-summary">
        <span className="rounded-full bg-blue-500/10 px-3 py-1 font-semibold text-blue-300">{filename}</span>
        <span className="rounded-full bg-emerald-500/10 px-3 py-1 font-semibold text-emerald-300" data-testid="import-mart-new">{fresh} new</span>
        <span className="rounded-full bg-slate-700/40 px-3 py-1 font-semibold text-slate-300" data-testid="import-mart-existing">{existing} already imported (will be updated)</span>
      </div>
      {isSample && (
        <div className="mx-5 mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="import-sample-warning">
          This is the RideStamp sample file with made-up riders. Explore the preview, then press <span className="font-semibold">Start over</span> — importing it would add 5 practice trips to your schedule.
        </div>
      )}
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-800 text-left font-mono-data text-[11px] uppercase tracking-wider text-slate-500">
            {["Trip #", "Rider", "Pickup", "Route", "Mobility", "Fare", ""].map((h) => <th key={h} className="px-4 py-2.5">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-800">
            {trips.map((t, i) => (
              <tr key={t.external_trip_id || i} data-testid={`import-mart-row-${i}`}>
                <td className="px-4 py-2.5 font-mono-data text-xs text-slate-300">{t.external_trip_id}</td>
                <td className="px-4 py-2.5 text-white">{t.rider_name}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-slate-300">{fmtDateTime(t.scheduled_pickup)}</td>
                <td className="min-w-[260px] px-4 py-2.5 text-xs text-slate-400">{t.pickup_address} → {t.dropoff_address}</td>
                <td className="px-4 py-2.5 text-xs text-slate-400">{(t.mobility_codes || []).join(", ") || t.trip_type}</td>
                <td className="px-4 py-2.5 text-slate-300">{t.price != null ? `$${Number(t.price).toFixed(2)}` : "—"}</td>
                <td className="px-4 py-2.5 text-xs">{t._exists ? <span className="text-slate-500">existing</span> : <span className="text-emerald-400">new</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 px-5 py-3">
        <span className="text-xs text-slate-500">Columns are read automatically from the MART layout. Trip numbers prevent duplicates.</span>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onReset} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="import-cancel">Cancel</Button>
          <Button onClick={onCommit} disabled={committing || total === 0} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="import-commit">
            {committing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Import {total - skip} trip{total - skip === 1 ? "" : "s"}{skip ? ` (skip ${skip})` : ""}
          </Button>
        </div>
      </div>
    </Panel>
  );
};
